// ThemeProvider — tema (light / dark / high-contrast) + skala teks/UI
// per-akun (US-SET-3, rfc-006 §7). Disimpan per akun di localStorage,
// diterapkan saat login; toggle cepat A-/A+ tersedia di shell.
//
// Penerapan: set data-theme + font-size (rem) di <html> sehingga seluruh
// sizing berbasis rem ikut skala (dukung zoom 200% + reflow).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./auth";

export type Theme = "light" | "dark" | "hc";

export const SCALE_MIN = 0.9;
export const SCALE_MAX = 1.3;
const SCALE_STEP = 0.06;

export type Pref = { theme: Theme; scale: number };
const DEFAULT_PREF: Pref = { theme: "light", scale: 1 };

const THEME_LABEL: Record<Theme, string> = {
  light: "Terang",
  dark: "Gelap",
  hc: "Kontras tinggi",
};

function prefKey(accountId: string | undefined): string {
  return `pos.pref.${accountId ?? "guest"}`;
}

function loadPref(accountId: string | undefined): Pref {
  try {
    const raw = localStorage.getItem(prefKey(accountId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Pref>;
      return {
        theme: parsed.theme ?? DEFAULT_PREF.theme,
        scale: clampScale(parsed.scale ?? DEFAULT_PREF.scale),
      };
    }
  } catch {
    // abaikan
  }
  return DEFAULT_PREF;
}

function clampScale(n: number): number {
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, +n.toFixed(2)));
}

type ThemeValue = {
  theme: Theme;
  scale: number;
  themeLabel: string;
  setTheme: (theme: Theme) => void;
  cycleTheme: () => void;
  incScale: () => void;
  decScale: () => void;
};

const ThemeContext = createContext<ThemeValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { account } = useAuth();
  const accountId = account?.id;
  const [pref, setPref] = useState<Pref>(() => loadPref(undefined));

  // Saat akun berganti (login/logout), muat preferensi akun tsb.
  useEffect(() => {
    setPref(loadPref(accountId));
  }, [accountId]);

  // Terapkan ke <html> + simpan per akun.
  useEffect(() => {
    const root = document.documentElement;
    if (pref.theme === "light") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", pref.theme);
    root.style.fontSize = `${pref.scale}rem`;
    try {
      localStorage.setItem(prefKey(accountId), JSON.stringify(pref));
    } catch {
      // abaikan
    }
  }, [pref, accountId]);

  const setTheme = useCallback((theme: Theme) => setPref((p) => ({ ...p, theme })), []);
  const cycleTheme = useCallback(
    () =>
      setPref((p) => ({
        ...p,
        theme: p.theme === "light" ? "dark" : p.theme === "dark" ? "hc" : "light",
      })),
    [],
  );
  const incScale = useCallback(
    () => setPref((p) => ({ ...p, scale: clampScale(p.scale + SCALE_STEP) })),
    [],
  );
  const decScale = useCallback(
    () => setPref((p) => ({ ...p, scale: clampScale(p.scale - SCALE_STEP) })),
    [],
  );

  const value = useMemo<ThemeValue>(
    () => ({
      theme: pref.theme,
      scale: pref.scale,
      themeLabel: THEME_LABEL[pref.theme],
      setTheme,
      cycleTheme,
      incScale,
      decScale,
    }),
    [pref, setTheme, cycleTheme, incScale, decScale],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme() harus dipakai di dalam <ThemeProvider>");
  return ctx;
}
